import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Pane,
  Heading,
  Switch,
  toaster,
  Text,
  Spinner,
  Paragraph,
  Card,
  Combobox,
} from 'evergreen-ui';
import { useQuery, useMutation, gql } from '@apollo/client';
import { GET_PUBLISHER_SETTINGS } from '../../graphql/queries/Publishers/QueryPublishers.js';
import UPDATE_PUBLISHER_SALES_REGION_SETTING from '../../graphql/mutations/Settings.js';
import { setpublisherGlobalData } from '../../redux/currentpublisher/publisherGlobalDataSlice.js';
import RegionManager from '../../components/settings/RegionManager';
import UPDATE_PUBLISHER_MUTATION from "../../graphql/mutations/PublisherData/UpdatePublisher.js";

function RoyaltyAndCurrencySettings({ activeSection }) {
  const dispatch = useDispatch();
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);

  const { data: publisherData, loading: publisherLoading, error: publisherError, refetch: refetchSettings } = useQuery(GET_PUBLISHER_SETTINGS, {
    variables: { publisherId: String(currentpublisher?.id) },
    skip: !currentpublisher?.id,
    fetchPolicy: "network-only", // Use network-only to ensure we're getting fresh data
  });

  const [updatePublisher] = useMutation(UPDATE_PUBLISHER_MUTATION);

  const [updateSalesRegionSetting, { loading: settingLoading, error: settingError }] = useMutation(
    UPDATE_PUBLISHER_SALES_REGION_SETTING,
    {
      refetchQueries: [{ query: GET_PUBLISHER_SETTINGS, variables: { publisherId: String(currentpublisher?.id) } }],
      awaitRefetchQueries: true,
      onError(error) {
        toaster.danger("Failed to update setting: " + error.message);
        if (publisherData?.publisher) {
            setEnableSalesByRegion(publisherData.publisher.enableSalesByRegion || false);
        }
      }
    }
  );

  const [enableSalesByRegion, setEnableSalesByRegion] = useState(false);

  useEffect(() => {
    if (publisherData?.publisher) {
      const enabled = publisherData.publisher.enableSalesByRegion || false;
      setEnableSalesByRegion(enabled);
      dispatch(setpublisherGlobalData({
        enableSalesByRegion: enabled,
        enable_sales_by_region: enabled
      }));
    }
  }, [publisherData, dispatch]);

  const handleSalesRegionToggle = useCallback(async (checked) => {
    setEnableSalesByRegion(checked);
    dispatch(setpublisherGlobalData({
      enableSalesByRegion: checked,
      enable_sales_by_region: checked
    }));
    try {
      const result = await updateSalesRegionSetting({
        variables: { enable: checked },
      });
      if (result.data?.updatePublisherSalesRegionSetting?.success) {
        toaster.success('Sales by region setting updated.');
        const confirmedEnabled = result.data.updatePublisherSalesRegionSetting.publisher.enableSalesByRegion;
        setEnableSalesByRegion(confirmedEnabled);
        dispatch(setpublisherGlobalData({
          enableSalesByRegion: confirmedEnabled,
          enable_sales_by_region: confirmedEnabled
        }));
      } else {
        toaster.danger('Failed to update setting (the server returned a failure response).');
        const originalState = publisherData?.publisher?.enableSalesByRegion || false;
        setEnableSalesByRegion(originalState);
        dispatch(setpublisherGlobalData({
          enableSalesByRegion: originalState,
          enable_sales_by_region: originalState
        }));
      }
    } catch (err) {
      console.error('Error updating sales region setting:', err);
      const originalState = publisherData?.publisher?.enableSalesByRegion || false;
      setEnableSalesByRegion(originalState);
      dispatch(setpublisherGlobalData({
        enableSalesByRegion: originalState,
        enable_sales_by_region: originalState
      }));
    }
  }, [updateSalesRegionSetting, dispatch, publisherData]);

  const accountingMethods = [
    { label: 'date items are paid', value: 'paid' },
    { label: 'date items are shipped', value: 'shipped' }
  ];

  const getAccountingMethod = () => {
    const methodValue = publisherData?.publisher?.accountingMethod || publisherGlobalData.accountingMethod;
    return accountingMethods.find(item => item.value === methodValue);
  }

  const handleSetCurrency = currency => {
    dispatch(setpublisherGlobalData({ currency: currency }));
    // Log the currency object to help debug
    console.log("Setting currency:", currency);
    
    updatePublisher({
      variables: {
        id: parseInt(currentpublisher.id),
        changed: JSON.stringify([{ key: "currency", val: currency.id }]),
      }
    })
    .then(response => {
      console.log("Currency update response:", response);
      if (response && response.data && response.data.updatePublisher) {
        toaster.success('Currency updated successfully');
        // Force a refetch with network-only policy to ensure we get fresh data
        refetchSettings({ fetchPolicy: 'network-only' });
      } else {
        toaster.danger("Failed to update currency: Unexpected response");
      }
    })
    .catch(err => {
      console.error("Currency update error:", err);
      toaster.danger("Failed to update currency: " + err.message);
    });
  }

  const handleSetAccounting = acct => {
    dispatch(setpublisherGlobalData({ accountingMethod: acct }));
    console.log("Setting accounting method:", acct);
    
    updatePublisher({
      variables: {
        id: parseInt(currentpublisher.id),
        changed: JSON.stringify([{ key: "accountingMethod", val: acct }]),
      }
    })
    .then(response => {
      console.log("Accounting method update response:", response);
      if (response && response.data && response.data.updatePublisher) {
        toaster.success('Accounting method updated successfully');
        // Force a refetch with network-only policy to ensure we get fresh data
        refetchSettings({ fetchPolicy: 'network-only' });
      } else {
        toaster.danger("Failed to update accounting method: Unexpected response");
      }
    })
    .catch(err => {
      console.error("Accounting method update error:", err);
      toaster.danger("Failed to update accounting method: " + err.message);
    });
  }

  if (publisherLoading) return <Spinner />;
  if (publisherError) return <Paragraph>Error loading settings: {publisherError.message}</Paragraph>;

  const currentCurrency = publisherData?.publisher?.currency || publisherGlobalData.currency;

  return (
    <Card border="default" padding={32} backgroundColor="white" elevation={1}>
      <Paragraph size={500} marginBottom={32}>
        {activeSection === "royaltyAccounting" ? "Royalty Accounting" : "Currency Settings"}
      </Paragraph>

      {activeSection === "royaltyAccounting" && (
        <Pane>
          <Pane marginY={8}>
            <Paragraph size={300} marginBottom={8}>When calculating royalties for product sales during a period, pay based on:</Paragraph>
            <Combobox
              initialSelectedItem={getAccountingMethod()}
              items={accountingMethods}
              itemToString={item => (item ? item.label : '')}
              onChange={selected => handleSetAccounting(selected.value)}
              width="100%"
            />
          </Pane>

          <Pane marginY={16} paddingTop={16} borderTop >
            <Paragraph size={300} marginBottom={8}>Do you have any contracts that require you to pay different royalties on the same ISBN for sales in different countries?</Paragraph>
            <Switch
              height={24}
              checked={enableSalesByRegion}
              onChange={(e) => handleSalesRegionToggle(e.target.checked)}
              disabled={settingLoading}
            />
            {settingLoading && <Spinner size={16} marginLeft={8}/>}
          </Pane>

          {enableSalesByRegion && (
            <RegionManager />
          )}
        </Pane>
      )}

      {activeSection === "currency" && (
        <Pane marginY={8}>
          <Paragraph size={300} marginBottom={8}>Select your preferred currency:</Paragraph>
          <Combobox
            initialSelectedItem={currentCurrency}
            items={publisherGlobalData.currencys || []}
            itemToString={item => (item ? `${item.label} (${item.symbol})` : '')}
            onChange={selected => handleSetCurrency(selected)}
            width="100%"
          />
        </Pane>
      )}
    </Card>
  );
}

export default RoyaltyAndCurrencySettings;
